import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'
import { cn } from '../lib/cn.js'

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-sunken text-foreground-muted border-border',
        primary: 'bg-primary-subtle text-primary border-transparent',
        success: 'bg-success-subtle text-success border-transparent',
        warning: 'bg-warning-subtle text-warning border-transparent',
        danger: 'bg-danger-subtle text-danger border-transparent',
        info: 'bg-info-subtle text-info border-transparent',
        violet: 'bg-violet-subtle text-violet border-transparent',
        teal: 'bg-teal-subtle text-teal border-transparent',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />
}

/**
 * Colour for each stage of the kosher provisioning chain. Kept here so the
 * queue, the unit detail page and the dashboard funnel all agree — a device
 * that is amber in one place must not be blue in another.
 */
export const UNIT_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  received: 'neutral',
  provisioning: 'info',
  verified: 'warning',
  sellable: 'success',
  reserved: 'primary',
  sold: 'primary',
  returned: 'warning',
  rma: 'danger',
  scrapped: 'danger',
  rented: 'primary',
  lost: 'danger',
}

export const ORDER_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  draft: 'neutral',
  pending_payment: 'warning',
  paid: 'success',
  fulfilling: 'info',
  shipped: 'info',
  ready_for_pickup: 'info',
  completed: 'success',
  cancelled: 'neutral',
  refunded: 'danger',
}

export const STOCK_REQUEST_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  requested: 'warning',
  accepted: 'info',
  fulfilled: 'success',
  declined: 'danger',
  cancelled: 'neutral',
}

/** A vendor's payment status on a purchase or add-on cost line. */
export const VENDOR_PAYMENT_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  unpaid: 'warning',
  invoiced: 'info',
  paid: 'success',
}

export const REPAIR_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  awaiting_dropoff: 'warning',
  intake: 'neutral',
  diagnosing: 'info',
  awaiting_parts: 'warning',
  in_repair: 'primary',
  ready_for_pickup: 'info',
  completed: 'success',
  cancelled: 'neutral',
}

export const RENTAL_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  active: 'primary',
  overdue: 'danger',
  returned: 'success',
  cancelled: 'neutral',
  not_returned: 'danger',
}

export const SUBSCRIPTION_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  incomplete: 'warning',
  active: 'success',
  past_due: 'danger',
  paused: 'warning',
  cancelled: 'neutral',
}

export const SUPPORT_TICKET_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  open: 'warning',
  pending: 'info',
  resolved: 'success',
  closed: 'neutral',
}

/**
 * Colour for the channel a support ticket came in on. Shown beside the status
 * badge, so `danger` is left unused (red reads as an alert) and `manual`, the
 * staff-entered catch-all, stays neutral. Each channel gets its own hue: call
 * is the brand green, so web chat must not be `success` (also green).
 */
export const SUPPORT_CHANNEL_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  call: 'primary',
  sms: 'info',
  web_chat: 'violet',
  contact_form: 'warning',
  email: 'teal',
  manual: 'neutral',
}

export const TASK_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  open: 'info',
  pending_review: 'warning',
  done: 'success',
}

/** Turns `ready_for_pickup` into `Ready for pickup`. */
export function humanizeStatus(status: string): string {
  const spaced = status.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
